/**
 * Milk derivation — the pure "milk is part of the meal" math that the
 * school order sheet (apps/ordo client), the field-trip placement manager
 * and the parent-order reconciler (apps/ordo server) all run against a
 * menu's system milk rows (`MenuItem.lineupMilkOptionCode`).
 *
 *   milkTotal          = ceil(mealTotal × BEVERAGE delivery rate)
 *   per-carrier weight = the menu's own SKU policy (below)
 *   per-carrier qty    = milkTotal apportioned by weight (largest remainder)
 *
 * It lives here — dependency-free — because the derivation used to exist
 * only on the client (`deriveMilkLines`), so a server-side writer of bagged /
 * field-trip lines had no milk derivation to call and shipped meals with no
 * standalone milk line at all (PROD-1244, Howard Street). The standalone
 * milk OrderItem is BOTH the milk count and the chef-pay carrier
 * (`packages/schema` milk-settlement), so every writer of meal lines must
 * derive it from the same function.
 *
 * ⚠ DO NOT "fix" `apportionQuantity` to round each key UP. The ordering
 * rule is that the KIND TOTAL rounds up (`milkTotalForMeals`); distributing
 * that already-rounded-up total must stay exact-sum, or the parts exceed the
 * whole: 10 milks across 3 varieties would become 4+4+4 = 12. Ruled on by
 * Sheila 2026-07-30. `MilkShareResolver.apportionByShares` (schema) carries
 * the same constraint.
 */

import { normalizeMilkOptionCode } from './milk-options'

/** Basis points meaning "one carton per meal" — the rate with no configured row. */
export const DEFAULT_MILK_RATE_BPS = 10000

/**
 * Milk total for a meal count: over-prep beats under-prep (ceil), and a
 * missing rate row is 100%. Mirrors `DeliveryRateResolver.expandShape`.
 */
export function milkTotalForMeals(mealTotal: number, rateBps: number | null | undefined): number {
  const bps = Number.isFinite(rateBps) && (rateBps as number) >= 0 ? (rateBps as number) : DEFAULT_MILK_RATE_BPS
  return mealTotal > 0 ? Math.ceil((mealTotal * bps) / 10000) : 0
}

/**
 * Largest-remainder apportionment of `total` units across weighted keys
 * (insertion order = tie-break order). Results always sum exactly to
 * `total`; weights normalize by their own sum (a non-positive sum falls back
 * to an even split). Weights may be percents, basis points or plain ratios —
 * only their proportions matter.
 */
export function apportionQuantity(total: number, weightByKey: Record<string, number>): Record<string, number> {
  const keys = Object.keys(weightByKey)
  const result: Record<string, number> = {}
  if (keys.length === 0) {
    return result
  }
  const safeTotal = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0
  const weights = keys.map((key) => {
    const w = weightByKey[key]
    return Number.isFinite(w) && (w as number) > 0 ? (w as number) : 0
  })
  let weightSum = weights.reduce((sum, w) => sum + w, 0)
  if (weightSum <= 0) {
    weights.fill(1)
    weightSum = keys.length
  }
  const remainders: Array<{ index: number; remainder: number }> = []
  let assigned = 0
  keys.forEach((key, index) => {
    const ideal = (safeTotal * (weights[index] as number)) / weightSum
    const base = Math.floor(ideal)
    result[key] = base
    assigned += base
    remainders.push({ index, remainder: ideal - base })
  })
  remainders.sort((a, b) => b.remainder - a.remainder || a.index - b.index)
  for (let i = 0; i < safeTotal - assigned; i++) {
    const slot = remainders[i % remainders.length] as { index: number }
    result[keys[slot.index] as string] = (result[keys[slot.index] as string] ?? 0) + 1
  }
  return result
}

/** Where a menu's milk selection came from (`Menu.settings.milkSelectionSource`). */
export type MilkSelectionSource = 'MENU' | 'SCHOOL_PROFILE' | 'TEMPLATE'

/** One system milk row on a menu — the ship/pay carrier for one milk SKU. */
export interface MenuMilkCarrier {
  menuItemId: string
  /** The milk SKU's Component id — keys the campus milk-share lookup. */
  componentId: string | null
  /** The carrier's canonical (or legacy) milk-option code. */
  milkOptionCode: string | null
}

export interface MenuMilkSkuPolicyInput {
  /**
   * `Menu.settings.milkSelectionSource`; null on legacy rows (treated as
   * "follow the campus shares").
   */
  milkSelectionSource: MilkSelectionSource | null | undefined
  /** `Menu.settings.milkOptionCodes` — the menu's declared lineup. */
  menuMilkOptionCodes: readonly string[] | null | undefined
  /** The menu's live system milk rows. */
  carriers: readonly MenuMilkCarrier[]
  /**
   * The campus's resolved milk shares per SKU Component id for the order
   * column being derived (percent or bps — proportions only). Missing =
   * unconfigured.
   */
  campusShareByComponentId: Readonly<Record<string, number>> | null | undefined
}

/**
 * The milk SKU policy for a menu's meals — ASSUMED (PROD-1244, flagged for
 * confirmation), implemented once so the sheet, the field-trip placer and the
 * repair script cannot disagree:
 *
 *   1. `milkSelectionSource === 'MENU'` with a non-empty lineup — the menu's
 *      EXPLICIT selection wins. Only carriers whose code is in the selection
 *      derive; the campus shares split among THOSE (renormalized), falling
 *      back to an even split when none of them has a configured share.
 *      Howard Street "Field Trip Meals" declares `["1_REGULAR"]`, so every
 *      milk is plain regardless of the campus's chocolate share.
 *   2. Otherwise (SCHOOL_PROFILE / TEMPLATE / legacy) — what the school
 *      sheet does for hot meals: every carrier derives at its campus share;
 *      an unconfigured SKU falls back to an even weight so the split still
 *      sums to the total.
 *
 * Returns a weight per carrier `menuItemId` — every carrier is present (a
 * zero weight means "derives to 0", the tombstone the sheet's sync relies on).
 * An explicit selection that matches NO carrier falls back to branch 2 rather
 * than deriving nothing: milk is included on the menu, so shipping none of it
 * would be the PROD-1244 failure again.
 */
export function menuMilkSkuWeights(input: MenuMilkSkuPolicyInput): Record<string, number> {
  const carriers = input.carriers
  const weights: Record<string, number> = {}
  if (carriers.length === 0) {
    return weights
  }
  const shares = input.campusShareByComponentId ?? {}
  const selected = new Set(
    (input.menuMilkOptionCodes ?? [])
      .map((code) => normalizeMilkOptionCode(code))
      .filter((code): code is NonNullable<typeof code> => code !== null),
  )
  const explicit =
    input.milkSelectionSource === 'MENU' && selected.size > 0
      ? carriers.filter((carrier) => {
          const code = carrier.milkOptionCode ? normalizeMilkOptionCode(carrier.milkOptionCode) : null
          return code !== null && selected.has(code)
        })
      : []
  const deriving = explicit.length > 0 ? explicit : [...carriers]
  const derivingIds = new Set(deriving.map((carrier) => carrier.menuItemId))
  const anyConfigured = deriving.some(
    (carrier) => carrier.componentId !== null && typeof shares[carrier.componentId] === 'number',
  )
  const even = 100 / deriving.length
  for (const carrier of carriers) {
    if (!derivingIds.has(carrier.menuItemId)) {
      weights[carrier.menuItemId] = 0
      continue
    }
    const configured = carrier.componentId !== null ? shares[carrier.componentId] : undefined
    // Unconfigured SKU inside a configured set → 0 (the school chose the
    // others); no configured SKU at all → even split. Mirrors the sheet's
    // `deriveMilkLines` and `MilkShareResolver.resolveShares`.
    weights[carrier.menuItemId] = anyConfigured ? (typeof configured === 'number' ? configured : 0) : even
  }
  return weights
}

export interface DeriveMenuMilkQuantitiesInput extends MenuMilkSkuPolicyInput {
  /** Meals ordered for the (menu, order column). */
  mealTotal: number
  /** BEVERAGE delivery rate in bps for the column; missing = 100%. */
  rateBps: number | null | undefined
}

/**
 * Per-carrier milk quantities for one (menu, order column): the meal count
 * through the BEVERAGE rate, split by the menu's SKU policy. Every carrier
 * is present in the result, zeros included.
 */
export function deriveMenuMilkQuantities(input: DeriveMenuMilkQuantitiesInput): Record<string, number> {
  const weights = menuMilkSkuWeights(input)
  if (Object.keys(weights).length === 0) {
    return {}
  }
  return apportionQuantity(milkTotalForMeals(input.mealTotal, input.rateBps), weights)
}

/** Counts for one order audience at one campus/date/mealtime. À-la-carte
 * milk is NOT system milk. Loose members of one meal bucket count once. */
export interface IncludedMilkLine {
  scope: string
  quantity: number
  systemMilk: boolean
  bundle: boolean
  bucket?: string | null
}

export interface IncludedMilkExcess {
  scope: string
  meals: number
  milk: number
}

export function includedMilkExcess(lines: readonly IncludedMilkLine[]): IncludedMilkExcess[] {
  const scopes = new Map<string, IncludedMilkLine[]>()
  for (const line of lines) {
    const group = scopes.get(line.scope) ?? []
    group.push(line)
    scopes.set(line.scope, group)
  }
  const excess: IncludedMilkExcess[] = []
  for (const [scope, group] of scopes) {
    const bundleBuckets = new Set(group.filter((line) => line.bundle && line.bucket).map((line) => line.bucket))
    const looseMeals = new Map<string, number>()
    let meals = 0
    let milk = 0
    for (const line of group) {
      const quantity = Math.max(0, line.quantity)
      if (line.systemMilk) {
        milk += quantity
      } else if (line.bundle) {
        meals += quantity
      } else if (line.bucket && !bundleBuckets.has(line.bucket)) {
        looseMeals.set(line.bucket, Math.max(looseMeals.get(line.bucket) ?? 0, quantity))
      }
    }
    meals += [...looseMeals.values()].reduce((sum, quantity) => sum + quantity, 0)
    if (milk > meals) {
      excess.push({ scope, meals, milk })
    }
  }
  return excess
}

export function includedMilkLimitMessage(excess: Pick<IncludedMilkExcess, 'milk' | 'meals'>): string {
  return `Included milk totals ${excess.milk}, but there are only ${excess.meals} mains. Reduce the combined milk quantities or increase the mains before saving.`
}
